/**
 * VP/LDS price contract — naming + comparability (no pricing math changes).
 *
 * searchCardPriceIls       — search results card only (frozen snapshot)
 * liveBookingPriceIls      — profile live total (supplements, handle fee, CC)
 * backendRoomComposePriceIls — server room compose only (no profile add-ons)
 */

export const VP_BACKEND_ROOM_COMPOSE_NOTE_HE =
  "backendRoomComposePriceIls אינו כולל תוספות פרופיל / דמי טיפול / כבודה שנבחרה.";

const LS_CONTRACT = "DEBUG_VP_PRICE_CONTRACT";

/** @type {Record<string, string>} */
const _lastContractFingerprintByDeal = Object.create(null);

function _isDev() {
  try {
    return import.meta.env?.DEV === true;
  } catch {
    return false;
  }
}

function _localVerbose() {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(LS_CONTRACT) === "1";
  } catch {
    return false;
  }
}

function _stableStringify(v) {
  try {
    return JSON.stringify(v, (_, val) =>
      typeof val === "bigint" ? String(val) : val
    );
  } catch {
    return "";
  }
}

export function _num(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function sumCheckedSupplementsIls(ldsSupplement) {
  if (!ldsSupplement || typeof ldsSupplement !== "object") return 0;
  let total = 0;
  for (const key of Object.keys(ldsSupplement)) {
    const row = ldsSupplement[key];
    if (!row?.checked) continue;
    const fare = _num(row.ourFare ?? row.fare);
    if (fare != null && fare > 0) total += fare;
  }
  return Math.round(total * 100) / 100;
}

export function roomCompositionFingerprint(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo)) return [];
  return roomsAndPaxInfo.map((room) => ({
    adults: Array.isArray(room?.adults) ? room.adults.length : 0,
    kids: Array.isArray(room?.kids) ? room.kids.length : 0,
    infants: Array.isArray(room?.infants) ? room.infants.length : 0,
  }));
}

export function countPassengersFromRooms(roomsAndPaxInfo) {
  if (!Array.isArray(roomsAndPaxInfo)) return 0;
  return roomsAndPaxInfo.reduce(
    (sum, room) =>
      sum +
      (Array.isArray(room?.adults) ? room.adults.length : 0) +
      (Array.isArray(room?.kids) ? room.kids.length : 0) +
      (Array.isArray(room?.infants) ? room.infants.length : 0),
    0
  );
}

/** @param {object|null|undefined} snapshot */
export function readSearchCardPriceFromSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") return null;
  const raw = snapshot.searchCardPriceIls ?? snapshot.resultsPriceIls;
  return _num(raw);
}

export function readSearchCardSourceFromSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") return null;
  return (
    snapshot.searchCardSourcePath ??
    snapshot.resultsSourcePath ??
    null
  );
}

export function resolveBackendRoomComposePriceIls(canonicalBackendProfilePricing) {
  const snap =
    canonicalBackendProfilePricing?.snapshot &&
    typeof canonicalBackendProfilePricing.snapshot === "object"
      ? canonicalBackendProfilePricing.snapshot
      : canonicalBackendProfilePricing?.syntheticOurPrice
          ?.canonicalPricingSnapshotV1 ?? null;
  return _num(snap?.totals?.finalPriceIls);
}

export function resolveLiveBookingPriceIls(ourPrice) {
  if (!ourPrice || typeof ourPrice !== "object") return null;
  return (
    _num(ourPrice.priceWithCommissionAndCreditCardFee) ??
    _num(ourPrice.priceWithCreditCardFee) ??
    _num(ourPrice.priceWithCommission)
  );
}

/**
 * @param {{
 *   resultsSnapshot?: object|null,
 *   roomsAndPaxInfo?: object[]|null,
 *   ldsSupplement?: object|null,
 *   ourPrice?: object|null,
 *   currencyRate?: unknown,
 *   canonicalBackendProfilePricing?: object|null,
 * }} ctx
 */
export function evaluateVpPriceContractComparability(ctx = {}) {
  const supplementTotal = sumCheckedSupplementsIls(ctx.ldsSupplement);
  const handleFee = _num(ctx.ourPrice?.handleFee) ?? 0;
  const checkedSupplementKeys =
    ctx.ldsSupplement && typeof ctx.ldsSupplement === "object"
      ? Object.keys(ctx.ldsSupplement).filter((k) => ctx.ldsSupplement[k]?.checked)
      : [];

  const profileRoomCount = Array.isArray(ctx.roomsAndPaxInfo)
    ? ctx.roomsAndPaxInfo.length
    : 0;
  const resultsRoomCount = _num(ctx.resultsSnapshot?.roomCount);
  const canonSnap =
    ctx.canonicalBackendProfilePricing?.snapshot &&
    typeof ctx.canonicalBackendProfilePricing.snapshot === "object"
      ? ctx.canonicalBackendProfilePricing.snapshot
      : null;
  const canonicalRoomCount = Array.isArray(canonSnap?.rooms)
    ? canonSnap.rooms.length
    : null;

  const profilePassengers = countPassengersFromRooms(ctx.roomsAndPaxInfo);
  const resultsPassengers = _num(ctx.resultsSnapshot?.passengerCount);

  const roomCountMatch =
    resultsRoomCount != null &&
    profileRoomCount === resultsRoomCount &&
    (canonicalRoomCount == null || canonicalRoomCount === profileRoomCount);

  const passengersMatch =
    resultsPassengers == null || resultsPassengers === profilePassengers;

  const profileFx =
    _num(ctx.ourPrice?.currencyRate) ?? _num(ctx.currencyRate) ?? null;
  const canonFx = _num(canonSnap?.supplier?.exchangeRate);
  const exchangeRateMatch =
    canonFx == null ||
    profileFx == null ||
    Math.abs(profileFx - canonFx) < 0.0001;

  /** @type {string[]} */
  const notComparableReasons = [];
  if (supplementTotal > 0) notComparableReasons.push("supplement_total_gt_0");
  if (handleFee > 0) notComparableReasons.push("handle_fee_gt_0");
  if (checkedSupplementKeys.length > 0) {
    notComparableReasons.push("checked_supplements_present");
  }
  if (!roomCountMatch) notComparableReasons.push("room_count_mismatch");
  if (!passengersMatch) notComparableReasons.push("passenger_count_mismatch");
  if (!exchangeRateMatch) notComparableReasons.push("exchange_rate_mismatch");

  const comparable = notComparableReasons.length === 0;

  return {
    comparable,
    notComparableReasons,
    roomCountMatch,
    passengersMatch,
    supplementTotal,
    handleFee,
    exchangeRateMatch,
    profileRoomCount,
    resultsRoomCount,
    canonicalRoomCount,
    profilePassengers,
    resultsPassengers,
    checkedSupplementKeys,
  };
}

/**
 * @param {{
 *   dealId?: string|null,
 *   searchCardPriceIls?: number|null,
 *   liveBookingPriceIls?: number|null,
 *   backendRoomComposePriceIls?: number|null,
 *   resultsSnapshot?: object|null,
 *   roomsAndPaxInfo?: object[]|null,
 *   ldsSupplement?: object|null,
 *   ourPrice?: object|null,
 *   currencyRate?: unknown,
 *   canonicalBackendProfilePricing?: object|null,
 *   liveBookingSourcePath?: string|null,
 *   searchCardSourcePath?: string|null,
 * }} input
 */
export function buildVpPriceContractTracePayload(input = {}) {
  const searchCardPriceIls = _num(input.searchCardPriceIls);
  const liveBookingPriceIls = _num(input.liveBookingPriceIls);
  const backendRoomComposePriceIls =
    _num(input.backendRoomComposePriceIls) ??
    resolveBackendRoomComposePriceIls(input.canonicalBackendProfilePricing);

  const contract = evaluateVpPriceContractComparability(input);

  const selectedUiPriceSource = "liveBookingPriceIls (packageProfileDisplayOurPrice → ourPrice)";

  return {
    dealId: input.dealId ?? null,
    searchCardPriceIls,
    liveBookingPriceIls,
    backendRoomComposePriceIls,
    comparable: contract.comparable,
    notComparableReasons: contract.notComparableReasons,
    roomCountMatch: contract.roomCountMatch,
    passengersMatch: contract.passengersMatch,
    supplementTotal: contract.supplementTotal,
    handleFee: contract.handleFee,
    exchangeRateMatch: contract.exchangeRateMatch,
    selectedUiPriceSource,
    backendRoomComposeNote: VP_BACKEND_ROOM_COMPOSE_NOTE_HE,
    liveBookingSourcePath: input.liveBookingSourcePath ?? null,
    searchCardSourcePath: input.searchCardSourcePath ?? null,
    profileRoomCount: contract.profileRoomCount,
    resultsRoomCount: contract.resultsRoomCount,
    canonicalRoomCount: contract.canonicalRoomCount,
  };
}

/**
 * @param {ReturnType<typeof buildVpPriceContractTracePayload>} payload
 * @param {{ force?: boolean }} [opts]
 */
export function logVpPriceContractTrace(payload, opts = {}) {
  if (!_isDev()) return;
  if (!payload || typeof payload !== "object") return;

  const dealKey = payload.dealId != null ? String(payload.dealId) : "(no-deal)";
  const fingerprint = _stableStringify({
    searchCardPriceIls: payload.searchCardPriceIls,
    liveBookingPriceIls: payload.liveBookingPriceIls,
    backendRoomComposePriceIls: payload.backendRoomComposePriceIls,
    comparable: payload.comparable,
    notComparableReasons: payload.notComparableReasons,
  });

  if (!opts.force && !_localVerbose() && _lastContractFingerprintByDeal[dealKey] === fingerprint) {
    return;
  }
  _lastContractFingerprintByDeal[dealKey] = fingerprint;

  // eslint-disable-next-line no-console
  console.info("[VP_PRICE_CONTRACT_TRACE]", payload);
}

/**
 * True when two prices may be compared for drift warnings (≥ ₪1).
 */
export function hasComparablePriceDrift(a, b, contract) {
  if (!contract?.comparable) return false;
  const x = _num(a);
  const y = _num(b);
  if (x == null || y == null) return false;
  return Math.abs(x - y) >= 1;
}
